import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {delay, Observable, of, retryWhen} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {TranslateLoader} from '@ngx-translate/core';
import {CoolLocalStorage} from '@angular-cool/storage';
import {environment} from '../../environments/environment';
import {StateService} from "./state.service";
import { isAfter, addDays } from 'date-fns';

@Injectable()
export class TranslationService implements TranslateLoader {
  constructor(private http: HttpClient,
              private _vault: CoolLocalStorage,
              private _state: StateService,
  ) {
  }

  getTranslation(lang: string): Observable<any> {
    if (lang === 'default') {
      return of('[]');
    }

    const id = this._state.translationId;
    if (id) {
      return this.fetchTranslation(lang, id);
    }

    // translationId not yet set — wait for the form to provide it before fetching
    return this._state.translationId$.pipe(
      filter(v => !!v),
      take(1),
      switchMap(resolvedId => this.fetchTranslation(lang, resolvedId))
    );
  }

  private fetchTranslation(lang: string, id: string): Observable<any> {
    const projectUrl = `${environment.translationUrl}/${id}/${lang}`;
    const hash = `${id}-${lang}`;
    const result = this._vault.getObject(hash);

    if (result && result['ttl'] && environment.production && isAfter(new Date(result['ttl']), new Date())) {
      return of(result);
    }

    return this.http.get(projectUrl).pipe(
      retryWhen(errors =>
        errors.pipe(
          delay(2000),
          take(10)
        )
      ),
      map((response: any) => {
        response.ttl = addDays(new Date(), 1).toISOString();
        try {
          this._vault.setObject(hash, response);
        } catch (e) {
          this._vault.clear();
        }
        return response;
      })
    );
  }


  addJobFieldTranslations(
    field: { id: string; translations?: any }
  ): void {
    const projectId = this._state.translationId || environment.translationId;

    const trans = field.translations || {};
    Object.keys(trans).forEach(lang => {
      const txt   = trans[lang];
      const hash  = `${projectId}-${lang}`;
      const stored = this._vault.getObject(hash) || {};
      stored[`jobMetaField.${field.id}.placeholder`] = txt.placeholder;
      stored[`jobMetaField.${field.id}.helptext`]    = txt.helptext;
      // @ts-ignore
      stored.ttl = addDays(new Date(), 1).toISOString();
      this._vault.setObject(hash, stored);
    });
  }
}
